import React from 'react';
import { styled } from '@youhelp-ru/ui';
import { useTheme } from '../../hooks/useTheme';
import { Box } from '../box';
import { Skeleton } from '../skeleton';
import { mergeSx } from '../../utils/sx/merge';
import { AvatarIconContent } from './components/content/icon';
import { AvatarProps } from './model';
import { AvatarLettersContent } from './components/content/letters';
import { AvatarTokens, name } from './const';
import { getAvatarSizeStyles, getAvatarSizeTokenStyles } from './utils';
import { AvatarImageContent } from './components/content/image';
import { AvatarContentBaseProps } from './components/content/model';
export type { AvatarProps } from './model';

const Root = styled(Box, { name })({
  position: 'relative',
  flexShrink: 0,
  flexBasis: AvatarTokens.Size('100px'),
  height: AvatarTokens.Size('100px'),
  maxHeight: AvatarTokens.Size('100px'),
  maxWidth: AvatarTokens.Size('100px'),
  width: AvatarTokens.Size('100px'),
});

export const Avatar: React.FC<AvatarProps> = ({
  rounded,
  content,
  size,
  contentBackgroundColor,
  contentColor,
  alt,
  sx,
  frameSx,
  lazyLoadProps,
  clickAction = 'default',
  onClick,
  iconProps,
}) => {
  const theme = useTheme();

  const contentBaseProps: AvatarContentBaseProps = {
    theme,
    color: contentColor,
    backgroundColor: contentBackgroundColor,
    clickAction,
    onClick,
  };

  return (
    <Root
      onClick={onClick}
      sx={mergeSx(
        {
          cursor: onClick ? 'pointer' : 'inherit',
        },
        size ? getAvatarSizeTokenStyles(size) : undefined,
        sx,
      )}
    >
      <Box
        sx={mergeSx(
          {
            borderRadius: rounded ? '50%' : { xs: `${theme.borderRadius.normal}px`, md: `${theme.borderRadius.medium}px` },
            overflow: 'hidden',
            position: 'relative',
          },
          getAvatarSizeStyles(),
          frameSx,
        )}
      >
        <Skeleton
          sx={{
            borderRadius: { xs: `${theme.borderRadius.normal}px`, md: `${theme.borderRadius.medium}px` },
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            transform: 'none',
            zIndex: 0,
          }}
        />
        {'letters' in content && content.letters ? (
          <AvatarLettersContent {...contentBaseProps} letters={content.letters} />
        ) : 'icon' in content && content.icon ? (
          <AvatarIconContent {...contentBaseProps} icon={content.icon} iconProps={iconProps} />
        ) : (
          <AvatarImageContent
            {...contentBaseProps}
            alt={alt}
            asset={'image' in content ? content.image : undefined}
            lazyLoadProps={lazyLoadProps}
          />
        )}
      </Box>
    </Root>
  );
};
